/**
 * SessionTimer Component
 * Shows a countdown toast when the session is about to expire.
 * Appears when < 5 minutes remain, with a "Stay logged in" button.
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Clock, RefreshCw, X } from 'lucide-react';

const SESSION_WARNING_THRESHOLD = 5 * 60 * 1000; // Show warning when < 5 min remain

const formatTime = (ms: number): string => {
  if (ms <= 0) return '0:00';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
};

const SessionTimer: React.FC = () => {
  const { isAuthenticated, sessionExpiresAt, manualRefresh, logout } = useAuth();
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const isRefreshingRef = React.useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !sessionExpiresAt) {
      setTimeRemaining(null);
      setDismissed(false);
      return;
    }

    const updateTimer = () => {
      const remaining = sessionExpiresAt - Date.now();
      setTimeRemaining(remaining);

      if (remaining <= 0 && !isRefreshingRef.current) {
        // Session expired — try one last refresh before logging out
        isRefreshingRef.current = true;
        manualRefresh().then((success) => {
          if (!success) {
            logout();
          }
        }).catch(() => {
          logout();
        }).finally(() => {
          isRefreshingRef.current = false;
        });
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [isAuthenticated, sessionExpiresAt, logout, manualRefresh]);

  // Reset dismissed state when sessionExpiresAt changes (i.e. token was refreshed)
  useEffect(() => {
    setDismissed(false);
  }, [sessionExpiresAt]);

  const handleStayLoggedIn = async () => {
    setIsRefreshing(true);
    isRefreshingRef.current = true;
    try {
      const success = await manualRefresh();
      if (success) {
        setDismissed(false); // Will re-evaluate with new expiry
      }
    } catch {
      // ignore
    } finally {
      setIsRefreshing(false);
      isRefreshingRef.current = false;
    }
  };

  // Don't show if not authenticated, no timer, dismissed, or plenty of time left
  if (
    !isAuthenticated ||
    timeRemaining === null ||
    dismissed ||
    timeRemaining > SESSION_WARNING_THRESHOLD ||
    timeRemaining <= 0
  ) {
    return null;
  }

  const urgencyColor = timeRemaining < 60000
    ? 'bg-red-50 border-red-300 text-red-800'
    : 'bg-amber-50 border-amber-300 text-amber-800';

  const iconColor = timeRemaining < 60000 ? 'text-red-500' : 'text-amber-500';

  return (
    <div className={`fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border shadow-lg p-4 transition-all animate-in slide-in-from-bottom-2 ${urgencyColor}`}>
      <div className="flex items-start gap-3">
        <Clock className={`h-5 w-5 mt-0.5 shrink-0 ${iconColor}`} />
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">Session expiring</p>
          <p className="text-sm mt-0.5 opacity-80">
            Your session expires in <span className="font-mono font-bold">{formatTime(timeRemaining)}</span>
          </p>
          <div className="flex gap-2 mt-3">
            <button
              onClick={handleStayLoggedIn}
              disabled={isRefreshing}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-primary text-white hover:brightness-110 transition disabled:opacity-50"
            >
              <RefreshCw className={`h-3.5 w-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Stay logged in'}
            </button>
            <button
              onClick={() => setDismissed(true)}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg border border-current/20 hover:bg-black/5 transition"
            >
              <X className="h-3.5 w-3.5" />
              Dismiss
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SessionTimer;
